import { MagnifyingGlass } from '@phosphor-icons/react';
import {
  ChangeEvent,
  KeyboardEvent,
  useEffect,
  useId,
  useRef,
  useState,
} from 'react';

import { cn } from '../../../../../helpers/classBuilders';
import { useClassHelper } from '../../../../../hooks/useClassHelper';
import { IServices, PlaceSuggestion } from '../../../../../types';
import { InputField } from '../../../../InputField/InputField';
import { componentName } from '../constants';
import { MappedPlace } from '../helpers/mapPlaceDetails';
import {
  MIN_QUERY_LENGTH,
  usePlacesAutocomplete,
} from '../hooks/usePlacesAutocomplete';
import { IValues } from '../types';
import { Paragraph } from './shared/Paragraph';

const DEBOUNCE_MS = 350;

interface Props {
  id: string;
  label: string;
  // Renders the required asterisk (new flow, Figma 2454-845).
  required?: boolean;
  placeholder: string;
  noResultsLabel: string;
  themeValues: IValues;
  services: Pick<IServices, 'placesAutocomplete' | 'placeDetails'>;
  regionCode: string;
  enabled: boolean;
  disabled?: boolean;
  isPortalPreview?: boolean;
  onSelect: (place: MappedPlace) => void;
  onResolveError?: (error: Error) => void;
  onSearchError?: (error: Error) => void;
  // Fires while a picked place is being resolved through Place Details, so the
  // parent can lock its manual fields — a late response would otherwise
  // overwrite whatever the user typed in the meantime.
  onResolvingChange?: (resolving: boolean) => void;
}

/**
 * "Find Your Address" — Google Places (New) typeahead for the VoWiFi address
 * form. Module-local on purpose: it is themed through the websheet branding
 * variables (not the portal design tokens) and only ever feeds this form.
 *
 * Predictions are display strings; the selected place is resolved through
 * Place Details `addressComponents` and handed to `onSelect` already mapped.
 */
export const AddressAutocomplete = ({
  id,
  label,
  required = false,
  placeholder,
  noResultsLabel,
  themeValues,
  services,
  regionCode,
  enabled,
  disabled,
  isPortalPreview,
  onSelect,
  onResolveError,
  onSearchError,
  onResolvingChange,
}: Props) => {
  const c = useClassHelper(componentName);
  const listboxId = `${useId()}-listbox`;

  const [query, setQuery] = useState('');
  const [open, setOpen] = useState(false);
  const [activeIndex, setActiveIndex] = useState(-1);
  const [resolving, setResolving] = useState(false);
  // Suppresses the debounced re-query right after a pick populated the form.
  const picked = useRef(false);
  // Explicit initial value: @types/react 19 removed the zero-arg useRef overload.
  const debounceRef = useRef<ReturnType<typeof setTimeout> | undefined>(
    undefined,
  );
  // Pick generation: a Place Details response that lands after a newer pick
  // (or after unmount) is dropped instead of overwriting the form.
  const pickSeq = useRef(0);
  const mounted = useRef(true);
  // Report each search error once — the parent callback identity may change
  // on every keystroke of the surrounding form.
  const lastReportedError = useRef<Error | null>(null);

  useEffect(() => {
    mounted.current = true;
    return () => {
      mounted.current = false;
    };
  }, []);

  const { suggestions, loading, error, search, resolve, clear } =
    usePlacesAutocomplete({
      services,
      regionCode,
      enabled: enabled && !isPortalPreview,
    });

  useEffect(() => {
    clearTimeout(debounceRef.current);
    if (picked.current || isPortalPreview) {
      return;
    }
    debounceRef.current = setTimeout(() => {
      search(query);
    }, DEBOUNCE_MS);
    return () => {
      clearTimeout(debounceRef.current);
    };
  }, [query, search, isPortalPreview]);

  useEffect(() => {
    if (!error || error === lastReportedError.current) {
      return;
    }
    lastReportedError.current = error;
    if (onSearchError) {
      onSearchError(error);
    }
  }, [error, onSearchError]);

  useEffect(() => {
    setActiveIndex(-1);
  }, [suggestions]);

  useEffect(() => {
    if (onResolvingChange) {
      onResolvingChange(resolving);
    }
  }, [resolving, onResolvingChange]);

  const trimmed = query.trim();
  const showNoResults =
    open &&
    !loading &&
    !error &&
    trimmed.length >= MIN_QUERY_LENGTH &&
    suggestions.length === 0 &&
    !picked.current;
  const expanded = open && (suggestions.length > 0 || showNoResults);

  const handleChange = (event: ChangeEvent<HTMLInputElement>) => {
    picked.current = false;
    setQuery(event.target.value);
    setOpen(true);
  };

  const handlePick = async (suggestion: PlaceSuggestion) => {
    picked.current = true;
    setOpen(false);
    clear();
    setQuery(suggestion.fullText || suggestion.mainText);
    setResolving(true);
    const seq = ++pickSeq.current;

    const isStale = () => {
      return !mounted.current || seq !== pickSeq.current;
    };

    try {
      const mapped = await resolve(suggestion.placeId);
      if (isStale()) {
        return;
      }
      onSelect(mapped);
    } catch (e) {
      if (!isStale() && onResolveError) {
        onResolveError(e as Error);
      }
    } finally {
      if (!isStale()) {
        setResolving(false);
      }
    }
  };

  const handleKeyDown = (event: KeyboardEvent<HTMLInputElement>) => {
    if (!expanded || suggestions.length === 0) {
      if (event.key === 'Escape') {
        setOpen(false);
      }
      return;
    }

    switch (event.key) {
      case 'ArrowDown': {
        event.preventDefault();
        setActiveIndex(index => {
          return (index + 1) % suggestions.length;
        });
        break;
      }
      case 'ArrowUp': {
        event.preventDefault();
        setActiveIndex(index => {
          return index <= 0 ? suggestions.length - 1 : index - 1;
        });
        break;
      }
      case 'Enter': {
        if (activeIndex >= 0) {
          event.preventDefault();
          handlePick(suggestions[activeIndex]);
        }
        break;
      }
      case 'Escape': {
        event.preventDefault();
        setOpen(false);
        break;
      }
      default:
        break;
    }
  };

  const handleBlur = () => {
    // Delay so an option `onMouseDown` pick still wins over the blur.
    setTimeout(() => {
      setOpen(false);
    }, 100);
  };

  return (
    <div className={c('__autocomplete')}>
      <InputField
        app="websheet"
        type="text"
        id={id}
        name={id}
        label={label}
        required={required}
        icon={<MagnifyingGlass size={16} aria-hidden="true" />}
        placeholder={placeholder}
        value={query}
        onChange={handleChange}
        onKeyDown={handleKeyDown}
        onFocus={() => {
          return setOpen(true);
        }}
        onBlur={handleBlur}
        autoComplete="off"
        role="combobox"
        aria-autocomplete="list"
        aria-expanded={expanded}
        aria-controls={listboxId}
        aria-activedescendant={
          activeIndex >= 0 ? `${listboxId}-option-${activeIndex}` : undefined
        }
        aria-busy={loading || resolving}
        disabled={disabled || resolving}
        aria-disabled={disabled || resolving}
      />

      {expanded && (
        <ul
          id={listboxId}
          role="listbox"
          aria-label={label}
          className={c('__autocomplete-list')}
        >
          {suggestions.map((suggestion, index) => {
            const isActive = index === activeIndex;
            return (
              <li
                key={suggestion.placeId}
                id={`${listboxId}-option-${index}`}
                role="option"
                aria-selected={isActive}
                className={cn(c('__autocomplete-option'), {
                  [c('__autocomplete-option--active')]: isActive,
                })}
                onMouseDown={event => {
                  event.preventDefault();
                  handlePick(suggestion);
                }}
                onMouseEnter={() => {
                  return setActiveIndex(index);
                }}
              >
                <Paragraph
                  fontFamily={themeValues.fontParagraph}
                  fontWeight="bold"
                  className={c('__autocomplete-option-main')}
                >
                  {suggestion.mainText}
                </Paragraph>
                {suggestion.secondaryText && (
                  <Paragraph
                    fontFamily={themeValues.fontParagraph}
                    className={c('__autocomplete-option-secondary')}
                  >
                    {suggestion.secondaryText}
                  </Paragraph>
                )}
              </li>
            );
          })}

          {showNoResults && (
            <li
              role="option"
              aria-selected={false}
              aria-disabled
              className={c(
                '__autocomplete-option',
                '__autocomplete-option--empty',
              )}
            >
              <Paragraph fontFamily={themeValues.fontParagraph}>
                {noResultsLabel}
              </Paragraph>
            </li>
          )}
        </ul>
      )}
    </div>
  );
};
